import { create } from 'zustand';
import {
  estadoInicial,
  reducirSesion,
  type EstadoSesion,
  type EventoSesion,
} from '../features/simulacion/sesionMachine';

/** Entradas que se conservan en el registro de diagnóstico. */
export const MAX_ENTRADAS_DIAGNOSTICO = 500;

export interface EntradaDiagnostico {
  /** `HH:MM:SS.mmm` local. */
  hora: string;
  /**
   * `transicion`: un evento de la máquina y el estado resultante.
   * `servidor`: un evento del socket, **ya pasado por el borde** (sin
   * `behavioral_cues`; D-13). `cliente`: algo que hizo la app (envío,
   * cierre, advertencias).
   */
  tipo: 'transicion' | 'servidor' | 'cliente';
  nombre: string;
  datos?: unknown;
}

interface SimulacionState {
  sesion: EstadoSesion;
  diagnostico: EntradaDiagnostico[];
  /** Aplica un evento a la máquina y registra la transición. */
  dispatch: (evento: EventoSesion) => void;
  registrar: (tipo: EntradaDiagnostico['tipo'], nombre: string, datos?: unknown) => void;
  /** Estado inicial para una sesión nueva o retomada; vacía el registro. */
  reiniciar: (modo: 'nuevo' | 'retomar') => void;
}

/**
 * Estado de la sesión activa (`04_ARQUITECTURA.md` §8.2). Es una cáscara
 * de Zustand sobre el reducer puro de `sesionMachine`: toda la lógica de
 * transiciones vive allá y se prueba sin DOM.
 */
export const useSimulacionStore = create<SimulacionState>((set, get) => ({
  sesion: estadoInicial(),
  diagnostico: [],
  dispatch: (evento) => {
    const antes = get().sesion;
    const despues = reducirSesion(antes, evento);
    const { tipo, ...datos } = evento;
    set({
      sesion: despues,
      diagnostico: agregar(get().diagnostico, {
        hora: horaActual(),
        tipo: 'transicion',
        nombre: tipo,
        datos: {
          ...(Object.keys(datos).length > 0 ? { evento: datos } : {}),
          fase: despues.fase,
          turno: despues.turno.etapa,
          ...(despues === antes ? { ignorado: true } : {}),
        },
      }),
    });
  },
  registrar: (tipo, nombre, datos) => {
    set({
      diagnostico: agregar(get().diagnostico, {
        hora: horaActual(),
        tipo,
        nombre,
        ...(datos !== undefined ? { datos } : {}),
      }),
    });
  },
  reiniciar: (modo) => set({ sesion: estadoInicial(modo), diagnostico: [] }),
}));

function agregar(lista: EntradaDiagnostico[], entrada: EntradaDiagnostico): EntradaDiagnostico[] {
  const siguiente = [...lista, entrada];
  return siguiente.length > MAX_ENTRADAS_DIAGNOSTICO
    ? siguiente.slice(siguiente.length - MAX_ENTRADAS_DIAGNOSTICO)
    : siguiente;
}

function horaActual(): string {
  const d = new Date();
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${dos(d.getHours())}:${dos(d.getMinutes())}:${dos(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}
